import { ISupplier, ISupplier_Type } from '@bridebook/models/source/models/Suppliers.types';
import type { IWedding } from '@bridebook/models/source/models/Weddings.types';
import type { IUISupplier } from '@bridebook/toolbox/src/types';
import gazetteer, { type CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { PremiumTiers } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';

const REQUIRED_TIER = PremiumTiers.Tier_2_5;
export function isBudgetMatch(
  budget: IWedding['budget'],
  supplierData: {
    countryCode: CountryCodes | undefined;
    targeting: { min: number; max: number };
    type: ISupplier_Type;
    tier: PremiumTiers | undefined;
  },
) {
  /**
   * Budget match logic from LIVE-24379.
   */
  if (budget == null || supplierData == null) {
    return false;
  }
  const { min, max } = supplierData.targeting;
  try {
    if (!supplierData.countryCode) {
      throw new Error('Supplier country code is required to perform budget match.');
    }
    const market = gazetteer.getMarketByCountry(supplierData.countryCode);

    if (market.flags.monetized !== true) {
      throw new Error('Budget match is only available on monetized markets.');
    }

    if (supplierData.type !== 'venue') {
      throw new Error('Budget match is only available for venues.');
    }

    if (!supplierData.tier || supplierData.tier < REQUIRED_TIER) {
      throw new Error(
        `Budget match is only available for venues with tier ${{ REQUIRED_TIER }} or higher.`,
      );
    }

    if (budget < market.budgetRange.min || budget > market.budgetRange.max) {
      return false;
    }
  } catch (error) {
    return false;
  }

  return budget >= min && budget <= max;
}

export const getSupplierTargetingBudget = (
  supplier: Pick<ISupplier, 'targeting'> | Pick<IUISupplier, 'targetingBudget'>,
) => {
  if ('targeting' in supplier) {
    return {
      min: supplier.targeting?.budget?.[0] || 0,
      max: supplier.targeting?.budget?.[1] || Infinity,
    };
  } else if ('targetingBudget' in supplier) {
    return {
      min:
        supplier.targetingBudget?.gte && supplier.targetingBudget.gte !== -1
          ? supplier.targetingBudget.gte
          : 0,
      max:
        supplier.targetingBudget?.lte && supplier.targetingBudget.lte !== -1
          ? supplier.targetingBudget.lte
          : Infinity,
    };
  } else {
    return { min: 0, max: 0 };
  }
};
